import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, InputNumber, Button, Card, Table, Tag, Space, Modal,
  Typography, message, Popconfirm, Tooltip,
} from 'antd';
import {
  ReloadOutlined, CopyOutlined, LogoutOutlined, SafetyCertificateOutlined,
} from '@ant-design/icons';
import { makeGetCall, makePostCall, getUserDetails } from '../../utils/helper';
import { TokenStorage } from '../../utils/cookieStorage';

const { Title, Text, Paragraph } = Typography;

const MAX_DAYS = 180;
const DEFAULT_DAYS = 90;

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const copy = (text, label = 'Copied') => {
  navigator.clipboard?.writeText(text);
  message.success(label);
};

const ResultRow = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
    <Text strong style={{ minWidth: 150 }}>{label}</Text>
    <Paragraph copyable={{ text: value }} style={{ margin: 0, fontFamily: 'monospace' }}>
      {value}
    </Paragraph>
  </div>
);

// Renew/Generate with an inline day picker
const RenewControl = ({ record, onRenew, disabled }) => {
  const [days, setDays] = useState(DEFAULT_DAYS);
  return (
    <Popconfirm
      title={record.hasLicense ? 'Regenerate license' : 'Generate license'}
      disabled={disabled}
      description={
        <Space direction="vertical" size={4}>
          <Text>Validity (days, max {MAX_DAYS})</Text>
          <InputNumber min={1} max={MAX_DAYS} value={days} onChange={(v) => setDays(v || DEFAULT_DAYS)} />
        </Space>
      }
      okText="Generate"
      onConfirm={() => onRenew(record, days)}
    >
      <Button size="small" icon={<ReloadOutlined />} disabled={disabled}>
        {record.hasLicense ? 'Renew' : 'Generate'}
      </Button>
    </Popconfirm>
  );
};

const LicenseDeployment = () => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [centers, setCenters] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [issuer, setIssuer] = useState(true);
  const [result, setResult] = useState(null);
  const [centerIdEdited, setCenterIdEdited] = useState(false);

  const user = getUserDetails();

  const fetchCenters = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await makeGetCall('/admin/centers');
      if (res.data.success) {
        setCenters(res.data.data || []);
        if (typeof res.data.isLicenseIssuer === 'boolean') setIssuer(res.data.isLicenseIssuer);
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to load centers');
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchCenters();
  }, [fetchCenters]);

  const handleLogout = () => {
    TokenStorage.clearAllTokens();
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = '/login';
  };

  const onNameChange = (e) => {
    if (!centerIdEdited) {
      form.setFieldsValue({ center_id: slugify(e.target.value) });
    }
  };

  const showResult = (data, { center_name, api_key, renewal }) => {
    setResult({
      center_id: data.centerId,
      center_name,
      api_key,
      deployUrl: data.deployUrl,
      issuedDate: data.issuedDate,
      expiryDate: data.expiryDate,
      days: data.days,
      renewal,
    });
  };

  const handleCreate = async (values) => {
    setSubmitting(true);
    try {
      const days = values.days || DEFAULT_DAYS;

      // 1. create the center row
      const createRes = await makePostCall('/admin/centers', {
        center_id: values.center_id,
        center_name: values.center_name,
        location: values.location,
        contact_email: values.contact_email,
      });
      if (!createRes.data.success) throw new Error(createRes.data.message || 'Failed to create center');

      const { center_id, center_name, api_key } = createRes.data.data;

      // 2. generate the license
      const licRes = await makePostCall(
        `/admin/centers/${encodeURIComponent(center_id)}/generate-license`,
        { days }
      );
      if (!licRes.data.success) throw new Error(licRes.data.message || 'Failed to generate license');

      showResult(licRes.data.data, { center_name, api_key, renewal: false });
      message.success('Center created and license generated');
      form.resetFields();
      setCenterIdEdited(false);
      fetchCenters();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRenew = async (record, days) => {
    try {
      const licRes = await makePostCall(
        `/admin/centers/${encodeURIComponent(record.center_id)}/generate-license`,
        { days: days || DEFAULT_DAYS }
      );
      if (!licRes.data.success) throw new Error(licRes.data.message || 'Failed to generate license');
      showResult(licRes.data.data, {
        center_name: record.center_name,
        api_key: record.api_key,
        renewal: record.hasLicense,
      });
      message.success('License generated');
      fetchCenters();
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || 'Failed to generate license');
    }
  };

  const statusTag = (record) => {
    if (!record.hasLicense) return <Tag>No license</Tag>;
    if (record.expired) return <Tag color="red">Expired</Tag>;
    if (record.daysUntilExpiry <= 7) return <Tag color="orange">{record.daysUntilExpiry}d left</Tag>;
    return <Tag color="green">{record.daysUntilExpiry}d left</Tag>;
  };

  const columns = [
    { title: 'Center ID', dataIndex: 'center_id', key: 'center_id' },
    { title: 'Name', dataIndex: 'center_name', key: 'center_name' },
    { title: 'Location', dataIndex: 'location', key: 'location', render: (v) => v || '—' },
    { title: 'Issued', dataIndex: 'license_issued_date', key: 'license_issued_date', render: (v) => v || '—' },
    { title: 'Expiry', dataIndex: 'license_expiry', key: 'license_expiry', render: (v) => v || '—' },
    { title: 'Status', key: 'status', render: (_, r) => statusTag(r) },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Space>
          <RenewControl record={r} onRenew={handleRenew} disabled={!issuer} />
          {r.deployUrl && (
            <Tooltip title="Copy deploy URL">
              <Button size="small" icon={<CopyOutlined />} onClick={() => copy(r.deployUrl, 'Deploy URL copied')} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];
  const filteredCenters = centers.filter(c =>
    (c.center_name || '').toLowerCase().includes(searchText.toLowerCase())  ||
    (c.center_id || '').toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div style={{ minHeight: '100%', background: '#F5F7FD' }}>
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 24px', background: '#fff', borderBottom: '1px solid #eee',
        }}
      >
        <Space>
          <SafetyCertificateOutlined style={{ fontSize: 20 }} />
          <Title level={4} style={{ margin: 0 }}>License Deployment</Title>
        </Space>
        <Space>
          <Text type="secondary">{user?.user_fullname || user?.username}</Text>
          <Button icon={<LogoutOutlined />} onClick={handleLogout}>Logout</Button>
        </Space>
      </div>

      <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
        {!issuer && (
          <Card style={{ marginBottom: 16, borderColor: '#faad14' }}>
            <Text type="warning">
              This server is not a license-issuing node (private key not found). License generation is disabled here.
            </Text>
          </Card>
        )}

        <Card title="New deployment" style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical" onFinish={handleCreate} initialValues={{ days: DEFAULT_DAYS }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0 16px' }}>
              <Form.Item label="Center Name" name="center_name" rules={[{ required: true, message: 'Enter center name' }]}>
                <Input placeholder="e.g., Apollo Hospital" onChange={onNameChange} />
              </Form.Item>
              <Form.Item
                label="Center ID"
                name="center_id"
                rules={[
                  { required: true, message: 'Enter center id' },
                  { pattern: /^[a-z0-9_]+$/i, message: 'Letters, numbers, underscores only' },
                  { max: 20, message: 'Center ID must be less than 20 characters' },
                ]}
              >
                <Input placeholder="e.g., apollo_hospital" onChange={() => setCenterIdEdited(true)} />
              </Form.Item>
              <Form.Item label="Location" name="location">
                <Input placeholder="Optional" />
              </Form.Item>
              <Form.Item label="Contact Email" name="contact_email" rules={[{ type: 'email', message: 'Invalid email' }]}>
                <Input placeholder="Optional" />
              </Form.Item>
              <Form.Item label={`License Days (max ${MAX_DAYS})`} name="days" rules={[{ required: true, message: 'Enter days' }]}>
                <InputNumber min={1} max={MAX_DAYS} style={{ width: '100%' }} />
              </Form.Item>
            </div>
            <Button type="primary" htmlType="submit" loading={submitting} disabled={!issuer}>
              Create Center &amp; Generate License
            </Button>
          </Form>
        </Card>

        <Card
          title="Centers"
          extra={
            <Space>
              <Input.Search
                placeholder="Search centers or center IDs..."
                allowClear
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 260 }}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchCenters}>Refresh</Button>
            </Space>
          }
        >
          <Table
            rowKey="center_id"
            columns={columns}
            dataSource={filteredCenters}
            loading={loadingList}
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} centers`,
              position: ['bottomRight'],
             }}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      </div>

      <Modal
        open={!!result}
        title={result?.renewal ? 'License regenerated' : 'Deployment ready'}
        onCancel={() => setResult(null)}
        footer={[<Button key="close" type="primary" onClick={() => setResult(null)}>Done</Button>]}
        width={680}
      >
        {result && (
          <div>
            <Paragraph type="secondary">
              Give these to the installer. Add the two values to the client <Text code>.env</Text>, then run the
              deploy URL on the client server.
            </Paragraph>
            <ResultRow label="CENTER_ID" value={result.center_id} />
            <ResultRow label="CENTER_API_KEY" value={result.api_key} />
            <div style={{ margin: '12px 0' }}>
              <Text strong>Validity: </Text>
              {result.issuedDate} → {result.expiryDate} ({result.days} days)
            </div>

            <Text strong>.env block</Text>
            <Paragraph
              copyable={{ text: `CENTER_ID=${result.center_id}\nCENTER_API_KEY=${result.api_key}` }}
              style={{ background: '#f6f6f6', padding: 12, borderRadius: 4, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}
            >
              {`CENTER_ID=${result.center_id}\nCENTER_API_KEY=${result.api_key}`}
            </Paragraph>

            <Text strong>Deploy URL (run on client server)</Text>
            <Paragraph
              copyable={{ text: result.deployUrl }}
              style={{ background: '#f6f6f6', padding: 12, borderRadius: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}
            >
              {result.deployUrl}
            </Paragraph>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LicenseDeployment;
